

<script lang="ts">
  import type { OrganizationDetail as OrganizationDetailApi } from "@kontextwerk/control-web-client"
  import type { PermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
  import type { CustomerFormModel, CustomersViewFrameContext } from "../../context"
  import type { PageTabWithId } from "@kontextwerk/web-sdk/core/navigation/viewNavigation"
  import type { PageContext } from "./navigation"
  import type { CustomerDetailsTabContext } from "./navigation"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import CustomerAvailabilitySection from "./_modules/customer-availability-section"
  import CustomerContactsSection from "./_modules/customer-contacts-section"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { CustomersViewFrameContext as ViewFrameContext } from "../../context"

  const customerTypeOptions = [
    { name: "Privatperson", value: "PERSON" },
    { name: "Unternehmen", value: "COMPANY" },
  ]

  const customerFacingNameModeOptions = [
    { name: "Name verwenden", value: "USE_DISPLAY_NAME" },
    { name: "Neutral ansprechen", value: "GENERIC" },
  ]

  const { viewFrameContext, activeTab } = $props<{
    viewFrameContext: ViewFrameContext
    activeTab: string
  }>()

  const pageContext = $derived.by<PageContext>(() => ({
    permissions: viewFrameContext.permissions,
    organization: viewFrameContext.organization,
    effectiveOrganizationId: viewFrameContext.effectiveOrganizationId,
    customer: viewFrameContext.customer,
    loading: viewFrameContext.loading,
    error: viewFrameContext.error,
    form: viewFrameContext.form,
    customerSaving: viewFrameContext.customerSaving,
    hasCustomerChanges: viewFrameContext.hasCustomerChanges,
    contactPoints: viewFrameContext.contactPoints,
    isFreeTrialExperience: viewFrameContext.isFreeTrialExperience,
    trialRestrictionsActive: viewFrameContext.trialRestrictionsActive,
    onContactsChanged: viewFrameContext.onContactsChanged,
    onRetry: viewFrameContext.onRetry,
    onResetForm: viewFrameContext.onResetForm,
    onSaveCustomer: viewFrameContext.onSaveCustomer,
    currentUserOrganizationId: viewFrameContext.currentUserOrganizationId,
  }))

  const canEditCustomer = $derived.by(() => {
    const belongsToOrganization = Boolean(
      pageContext.currentUserOrganizationId &&
      pageContext.organization?.id &&
      pageContext.currentUserOrganizationId === pageContext.organization.id,
    )

    // Registry edits stay inside the confirmed organization; staff need a delegated membership.
    return Boolean(
      !pageContext.trialRestrictionsActive &&
      !pageContext.customer?.archived_at &&
      belongsToOrganization &&
      pageContext.permissions.allows("kehrblatt.registry", "manage"),
    )
  })

  const tabContext = $derived.by<CustomerDetailsTabContext>(() => ({
    ...pageContext,
    canEditCustomer,
  }))

  const saveCustomerIfChanged = (ctx: CustomerDetailsTabContext) => {
    if (!ctx.canEditCustomer || !ctx.hasCustomerChanges || ctx.customerSaving) return

    ctx.onSaveCustomer(ctx.canEditCustomer)
  }

  const saveCustomerAfterBlur = (ctx: CustomerDetailsTabContext, event: FocusEvent) => {
    const target = event.currentTarget as HTMLElement | null
    const relatedTarget = event.relatedTarget as Node | null

    if (target && relatedTarget && target.contains(relatedTarget)) return

    saveCustomerIfChanged(ctx)
  }
</script>

{#snippet detailsContent(ctx: CustomerDetailsTabContext)}
  {#if ctx.loading}
    <div class="state-block">
      <p>Ansprechpartner wird geladen…</p>
    </div>
  {:else if ctx.error}
    <div class="state-block">
      <p>{ctx.error}</p>
      <CTA type="secondaryButton" content="Erneut versuchen" onClick={ctx.onRetry} />
    </div>
  {:else if ctx.customer}
    <SectionTitle
      title="Ansprechpartner"
      brightBackground={true}
      styleOuter="margin-bottom: 1.2rem;"
    />
    <div class="details-grid form">
      <div class="row row-three">
        <div onfocusout={(event) => saveCustomerAfterBlur(ctx, event)}>
          <Input
            placeholder="Anzeigename"
            bind:value={ctx.form.display_name}
            disabled={!ctx.canEditCustomer}
            tooltip="Der Ansprechpartner wird beim Terminvereinbaren über diesen Namen verifiziert. Stellen Sie sicher, dass er korrekt ist."
          />
        </div>
        <Input
          type="select"
          placeholder="Typ"
          options={customerTypeOptions}
          bind:value={ctx.form.type}
          disabled={!ctx.canEditCustomer}
          brightBackground={true}
          onChange={() => saveCustomerIfChanged(ctx)}
        />
        <Input
          type="select"
          placeholder="Kundenansprache"
          options={customerFacingNameModeOptions}
          bind:value={ctx.form.customer_facing_name_mode}
          disabled={!ctx.canEditCustomer}
          brightBackground={true}
          tooltip="Steuert, ob KehrBlatt diesen Namen in E-Mails, Telefonaten und Briefen verwendet."
          onChange={() => saveCustomerIfChanged(ctx)}
        />
      </div>
      <div class="row row-full">
        <div onfocusout={(event) => saveCustomerAfterBlur(ctx, event)}>
          <Input
            placeholder="Notizen (optional)"
            type="textarea"
            bind:value={ctx.form.notes}
            disabled={!ctx.canEditCustomer}
          />
        </div>
      </div>
    </div>
  {/if}
{/snippet}

{#snippet contactsContent(ctx: CustomerDetailsTabContext)}
  {#if ctx.loading}
    <div class="state-block">
      <p>Ansprechpartner wird geladen…</p>
    </div>
  {:else if ctx.error}
    <div class="state-block">
      <p>{ctx.error}</p>
      <CTA type="secondaryButton" content="Erneut versuchen" onClick={ctx.onRetry} />
    </div>
  {:else if ctx.customer}
    <div class="details-grid form">
      <CustomerContactsSection
        contacts={ctx.contactPoints}
        organizationId={ctx.effectiveOrganizationId ?? ""}
        customerId={ctx.customer.id}
        canEdit={ctx.canEditCustomer}
        onChanged={ctx.onContactsChanged}
      />
    </div>
  {/if}
{/snippet}

{#snippet availabilityContent(ctx: CustomerDetailsTabContext)}
  {#if ctx.loading}
    <div class="state-block">
      <p>Ansprechpartner wird geladen…</p>
    </div>
  {:else if ctx.error}
    <div class="state-block">
      <p>{ctx.error}</p>
      <CTA type="secondaryButton" content="Erneut versuchen" onClick={ctx.onRetry} />
    </div>
  {:else if ctx.customer}
    <div class="details-grid form">
      <CustomerAvailabilitySection customerId={ctx.customer.id} canEdit={ctx.canEditCustomer} />
    </div>
  {/if}
{/snippet}

{#if activeTab === "details"}
  {@render detailsContent(tabContext)}
{:else if activeTab === "contacts"}
  {@render contactsContent(tabContext)}
{:else if activeTab === "availability"}
  {@render availabilityContent(tabContext)}
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .state-block {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .details-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .row {
    display: grid;
    gap: 1rem;
    align-items: start;
  }

  .row-three {
    grid-template-columns: minmax(0, 1.4fr) minmax(12rem, 0.8fr) minmax(14rem, 0.8fr);
  }

  .row-full {
    grid-template-columns: 1fr;
  }

  @media (max-width: 900px) {
    .row-three {
      grid-template-columns: 1fr;
    }
  }
</style>
